'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const DEAL_TERMS = [
{ value: '$25K–$500K', label: 'Typical EMD Range' },
{ value: '48–72 hrs', label: 'Funding Speed' },
{ value: 'Fully Refundable', label: 'Eligible Deposits' },
{ value: 'None', label: 'Personal Guarantee' }
];

const SECTORS = ['CRE', 'Oil & Gas', '1031 Exchange', 'Asset Purchases'];

// ─── Lead Form ────────────────────────────────────────────────────────────────

interface LeadFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  emdAmount: string;
  targetFundingDate: string;
}

type LeadFormErrors = Partial<Record<keyof LeadFormData, string>>;

const emptyLead: LeadFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  emdAmount: '',
  targetFundingDate: ''
};

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatCurrency(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return '$' + Number(digits).toLocaleString('en-US');
}

function validateLead(form: LeadFormData): LeadFormErrors {
  const errors: LeadFormErrors = {};
  if (!form.firstName.trim()) errors.firstName = 'Required';
  if (!form.lastName.trim()) errors.lastName = 'Required';
  if (!form.email.trim()) {
    errors.email = 'Required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = 'Enter a valid email';
  }
  if (!form.phoneNumber.trim()) {
    errors.phoneNumber = 'Required';
  } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(form.phoneNumber.trim())) {
    errors.phoneNumber = 'Enter a valid phone number';
  }
  if (!form.emdAmount.trim()) errors.emdAmount = 'Required';
  if (!form.targetFundingDate.trim()) {
    errors.targetFundingDate = 'Required';
  } else {
    const selected = new Date(form.targetFundingDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selected < today) errors.targetFundingDate = 'Date cannot be in the past';
  }
  return errors;
}

function getTodayString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Shared input class for the hero form
const heroInputBase: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '2px',
  color: 'rgba(255,255,255,0.88)',
  fontFamily: 'var(--font-body)',
  fontSize: '0.8125rem',
  padding: '9px 11px',
  transition: 'border-color 0.2s'
};

function HeroInput({
  label,
  id,
  error,
  children





}: {label: string;id: string;error?: string;children: React.ReactNode;}) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: '8px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.38em',
          color: error ? 'rgba(239,100,100,0.9)' : 'rgba(255,255,255,0.50)',
          fontFamily: 'var(--font-body)',
          marginBottom: '5px'
        }}>

        {label}
      </label>
      {children}
      {error &&
      <p
        id={errorId}
        style={{
          marginTop: '4px',
          fontSize: '10px',
          color: 'rgba(239,100,100,0.9)',
          fontFamily: 'var(--font-body)'
        }}
        role="alert">

          {error}
        </p>
      }
    </div>);

}

function HeroLeadForm({ revealed }: {revealed: boolean;}) {
  const [form, setForm] = useState<LeadFormData>(emptyLead);
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof LeadFormData, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [todayStr, setTodayStr] = useState('');
  const firstErrorRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTodayStr(getTodayString());
  }, []);

  function handleChange(field: keyof LeadFormData, raw: string) {
    let value = raw;
    if (field === 'phoneNumber') value = formatPhone(raw);
    if (field === 'emdAmount') value = formatCurrency(raw);
    const next = { ...form, [field]: value };
    setForm(next);
    if (touched[field]) {
      const errs = validateLead(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  }

  function handleBlur(field: keyof LeadFormData) {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validateLead(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const allTouched = Object.keys(emptyLead).reduce(
      (acc, k) => ({ ...acc, [k]: true }),
      {} as Record<keyof LeadFormData, boolean>
    );
    setTouched(allTouched);
    const errs = validateLead(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      // Move focus to first invalid field
      const fieldOrder: (keyof LeadFormData)[] = ['firstName', 'lastName', 'email', 'phoneNumber', 'emdAmount', 'targetFundingDate'];
      const firstErrField = fieldOrder.find((f) => errs[f]);
      if (firstErrField) {
        const el = document.getElementById(`hero-${firstErrField}`);
        if (el) (el as HTMLElement).focus();
      }
      return;
    }

    setSubmitting(true);
    try {
      const rawEmd = form.emdAmount.replace(/[^0-9]/g, '');

      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        emdAmount: rawEmd,
        closingDate: form.targetFundingDate,
        industry: 'Other',
        purchasePrice: rawEmd,
        dealDescription: '',
        company: '',
        howHeard: '',
        hasLoi: false
      };

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setSubmitStatus('success');
        setForm(emptyLead);
        setTouched({});
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle = (field: keyof LeadFormData): React.CSSProperties => ({
    ...heroInputBase,
    borderColor: touched[field] && errors[field] ?
    'rgba(239,100,100,0.7)' :
    touched[field] && !errors[field] && form[field] ?
    'rgba(82,183,136,0.45)' :
    'rgba(255,255,255,0.14)'
  });

  if (submitStatus === 'success') {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          opacity: revealed ? 1 : 0,
          transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.38s',
          padding: '20px 0',
          borderTop: '1px solid rgba(82,183,136,0.14)',
          borderBottom: '1px solid rgba(82,183,136,0.14)'
        }}>

        <div className="flex items-start gap-3">
          <div
            style={{
              flexShrink: 0,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: 'rgba(82,183,136,0.18)',
              border: '1px solid rgba(82,183,136,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '1px'
            }}>

            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5l2.5 2.5L8 3" stroke="rgba(82,183,136,0.9)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <p
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                color: 'rgba(82,183,136,0.9)',
                fontFamily: 'var(--font-body)',
                marginBottom: '4px'
              }}>

              Submission Received
            </p>
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.65)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1.5
              }}>

              We'll review your deal and follow up shortly.
            </p>
          </div>
        </div>
      </div>);

  }

  return (
    <div
      style={{
        opacity: revealed ? 1 : 0,
        transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.28s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.28s',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(201,168,76,0.30)',
        borderRadius: '8px',
        padding: '24px'
      }}>

      {/* Form header */}
      <div style={{ marginBottom: '14px' }}>
        <div className="flex items-center gap-3" style={{ marginBottom: '6px' }}>
          <div className="w-4 h-px" style={{ background: 'rgba(201,168,76,0.5)', flexShrink: 0 }} />
          <p
            style={{
              fontSize: '8px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.38em',
              color: 'rgba(201,168,76,0.75)',
              fontFamily: 'var(--font-body)'
            }}>

            Quick Start
          </p>
        </div>
        <h2
          style={{
            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.88)',
            fontFamily: 'var(--font-body)',
            marginBottom: '3px'
          }}>

          Ready to fund your EMD?
        </h2>
        <p
          style={{
            fontSize: '11px',
            color: 'rgba(255,255,255,0.48)',
            fontFamily: 'var(--font-body)'
          }}>Tell us about your deal and we'll follow up quickly. All fields are required.


        </p>
      </div>

      {/* Error banner */}
      {submitStatus === 'error' &&
      <div
        role="alert"
        aria-live="assertive"
        style={{
          marginBottom: '12px',
          padding: '9px 12px',
          background: 'rgba(239,100,100,0.08)',
          border: '1px solid rgba(239,100,100,0.25)',
          borderRadius: '2px'
        }}>

          <p
          style={{
            fontSize: '11px',
            color: 'rgba(239,100,100,0.9)',
            fontFamily: 'var(--font-body)'
          }}>

            Something went wrong. Please try again — your information has been preserved.
          </p>
        </div>
      }

      <form onSubmit={handleSubmit} noValidate aria-label="Quick Start deal form">
        {/* 2-col grid on sm+, single col on mobile */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px 12px'
          }}
          className="grid-cols-1 sm:grid-cols-2">

          {/* Row 1 */}
          <HeroInput label="First Name" id="hero-firstName" error={errors.firstName}>
            <input
              id="hero-firstName"
              type="text"
              autoComplete="given-name"
              required
              value={form.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              placeholder="Jane"
              style={inputStyle('firstName')}
              disabled={submitting}
              aria-invalid={touched.firstName && !!errors.firstName ? true : undefined}
              aria-describedby={touched.firstName && errors.firstName ? 'hero-firstName-error' : undefined} />

          </HeroInput>

          <HeroInput label="Last Name" id="hero-lastName" error={errors.lastName}>
            <input
              id="hero-lastName"
              type="text"
              autoComplete="family-name"
              required
              value={form.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              placeholder="Smith"
              style={inputStyle('lastName')}
              disabled={submitting}
              aria-invalid={touched.lastName && !!errors.lastName ? true : undefined}
              aria-describedby={touched.lastName && errors.lastName ? 'hero-lastName-error' : undefined} />

          </HeroInput>

          {/* Row 2 */}
          <HeroInput label="Email" id="hero-email" error={errors.email}>
            <input
              id="hero-email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder="jane@example.com"
              style={inputStyle('email')}
              disabled={submitting}
              aria-invalid={touched.email && !!errors.email ? true : undefined}
              aria-describedby={touched.email && errors.email ? 'hero-email-error' : undefined} />

          </HeroInput>

          <HeroInput label="Phone Number" id="hero-phoneNumber" error={errors.phoneNumber}>
            <input
              id="hero-phoneNumber"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              required
              value={form.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              onBlur={() => handleBlur('phoneNumber')}
              placeholder="(555) 000-0000"
              style={inputStyle('phoneNumber')}
              disabled={submitting}
              aria-invalid={touched.phoneNumber && !!errors.phoneNumber ? true : undefined}
              aria-describedby={touched.phoneNumber && errors.phoneNumber ? 'hero-phoneNumber-error' : undefined} />

          </HeroInput>

          {/* Row 3 */}
          <HeroInput label="EMD Amount" id="hero-emdAmount" error={errors.emdAmount}>
            <input
              id="hero-emdAmount"
              type="text"
              inputMode="decimal"
              required
              value={form.emdAmount}
              onChange={(e) => handleChange('emdAmount', e.target.value)}
              onBlur={() => handleBlur('emdAmount')}
              placeholder="$50,000"
              style={inputStyle('emdAmount')}
              disabled={submitting}
              aria-invalid={touched.emdAmount && !!errors.emdAmount ? true : undefined}
              aria-describedby={touched.emdAmount && errors.emdAmount ? 'hero-emdAmount-error' : undefined} />

          </HeroInput>

          <HeroInput label="Target Funding Date" id="hero-targetFundingDate" error={errors.targetFundingDate}>
            <input
              id="hero-targetFundingDate"
              type="date"
              required
              value={form.targetFundingDate}
              min={todayStr}
              onChange={(e) => handleChange('targetFundingDate', e.target.value)}
              onBlur={() => handleBlur('targetFundingDate')}
              style={{
                ...inputStyle('targetFundingDate'),
                colorScheme: 'dark'
              }}
              disabled={submitting}
              aria-invalid={touched.targetFundingDate && !!errors.targetFundingDate ? true : undefined}
              aria-describedby={touched.targetFundingDate && errors.targetFundingDate ? 'hero-targetFundingDate-error' : undefined} />

          </HeroInput>
        </div>

        {/* Submit */}
        <div style={{ marginTop: '12px' }}>
          <button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            data-focus-dark
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '11px 28px',
              background: submitting ? 'rgba(201,168,76,0.5)' : '#C9A84C',
              color: submitting ? 'rgba(13,31,23,0.6)' : '#0D1F17',
              border: 'none',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.25em',
              fontFamily: 'var(--font-body)',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s, opacity 0.2s',
              width: '100%'
            }}>

            {submitting ?
            <>
                <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                style={{ animation: 'spin 0.8s linear infinite' }}
                aria-hidden="true">

                  <circle cx="6" cy="6" r="5" stroke="rgba(13,31,23,0.4)" strokeWidth="1.5" />
                  <path d="M6 1a5 5 0 0 1 5 5" stroke="rgba(13,31,23,0.8)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Submitting…
              </> :

            'SUMBIT MY DEAL'
            }
          </button>
        </div>

        {/* Selling points — reassurance beneath the submit button */}
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '12px 20px'
          }}>

          {[
          { label: 'No debt obligation' },
          { label: 'No equity given up' }].
          map((item) =>
          <div key={item.label} className="flex items-center gap-2">
              <div
              style={{
                width: '3px',
                height: '3px',
                borderRadius: '50%',
                background: 'rgba(82,183,136,0.75)',
                flexShrink: 0
              }} />

              <span
              style={{
                fontSize: '9px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                color: 'rgba(255,255,255,0.50)',
                fontFamily: 'var(--font-body)'
              }}>

                {item.label}
              </span>
            </div>
          )}
        </div>
      </form>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        #hero-targetFundingDate::-webkit-calendar-picker-indicator { filter: invert(0.6); cursor: pointer; }
        #hero-emdAmount::placeholder,
        #hero-firstName::placeholder,
        #hero-lastName::placeholder,
        #hero-email::placeholder,
        #hero-phoneNumber::placeholder { color: rgba(255,255,255,0.55); }
        #hero-firstName:focus-visible,
        #hero-lastName:focus-visible,
        #hero-email:focus-visible,
        #hero-phoneNumber:focus-visible,
        #hero-emdAmount:focus-visible,
        #hero-targetFundingDate:focus-visible {
          outline: 3px solid #52B788;
          outline-offset: 2px;
        }
      `}</style>
    </div>);

}

// ─── Full-width Deal Parameters Strip ────────────────────────────────────────

function DealParametersStrip({ revealed }: {revealed: boolean;}) {
  return (
    <div
      style={{
        opacity: revealed ? 1 : 0,
        transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.50s',
        marginTop: '22px'
      }}>

      {/* Eyebrow row */}
      <div className="flex items-center gap-3" style={{ marginBottom: '0' }}>
        <div className="w-4 h-px flex-shrink-0" style={{ background: 'rgba(201,168,76,0.5)' }} />
        <span
          style={{
            fontSize: '8px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.38em',
            color: 'rgba(201,168,76,0.80)',
            fontFamily: 'var(--font-body)'
          }}>

          Deal Parameters
        </span>
      </div>

      {/* Metrics band */}
      <div
        style={{
          marginTop: '10px',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(0,20,10,0.18)',
          paddingTop: '22px',
          paddingBottom: '22px'
        }}>

        {/*
           Responsive grid:
           - xl (≥1280px): 5 columns
           - md–xl (768–1279px): 3 columns (last 2 wrap naturally → 3+2)
           - <md: 2 columns (last item spans full width via :last-child rule)
          */}
        <dl
          className="deal-params-grid"
          style={{
            display: 'grid',
            margin: 0,
            padding: 0
          }}>

          {DEAL_TERMS.map((term, idx) =>
          <div
            key={term.label}
            className="deal-param-item"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '0 16px',
              position: 'relative'
            }}>

              {/* Vertical separator — rendered as a pseudo-element via CSS */}
              {/* Value — displayed first visually via order:1 */}
              <dd
              className="font-display deal-param-value"
              style={{
                order: 1,
                margin: 0,
                fontSize: 'clamp(1.0625rem, 2.2vw, 1.375rem)',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                color: term.label === 'Typical EMD Range' ? '#C9A84C' : 'rgba(255,255,255,0.88)',
                marginBottom: '6px'
              }}>

                {term.value}
              </dd>
              {/* Term — displayed second visually via order:2 */}
              <dt
              style={{
                order: 2,
                margin: 0,
                fontSize: '7.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.30em',
                color: 'rgba(255,255,255,0.50)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1.3
              }}>

                {term.label}
              </dt>
            </div>
          )}
        </dl>
      </div>

      {/* Footnote */}
      <div
        className="flex items-start gap-2"
        style={{ marginTop: '13px' }}>

        <div
          style={{
            flexShrink: 0,
            width: '3px',
            height: '3px',
            borderRadius: '50%',
            background: 'rgba(201,168,76,0.45)',
            marginTop: '5px'
          }} />

        <p
          style={{
            fontSize: '10px',
            lineHeight: 1.5,
            color: 'rgba(255,255,255,0.55)',
            fontFamily: 'var(--font-body)'
          }}>

          Funding available only while the EMD remains{' '}
          <span style={{ color: 'rgba(201,168,76,0.80)' }}>fully refundable</span>{' '}
          under the purchase agreement.
        </p>
      </div>

      <style>{`
        /* 4-column layout on large desktop */
        @media (min-width: 1280px) {
          .deal-params-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
          .deal-param-item + .deal-param-item {
            border-left: 1px solid rgba(255,255,255,0.08);
          }
        }
        /* 2×2 layout on medium tablet */
        @media (min-width: 768px) and (max-width: 1279px) {
          .deal-params-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            row-gap: 20px;
          }
          /* Separators: right column items get left border */
          .deal-param-item:nth-child(2),
          .deal-param-item:nth-child(4) {
            border-left: 1px solid rgba(255,255,255,0.08);
          }
          /* Top separator for row 2 */
          .deal-param-item:nth-child(3),
          .deal-param-item:nth-child(4) {
            padding-top: 20px;
            border-top: 1px solid rgba(255,255,255,0.05);
          }
        }
        /* 2-column layout on mobile */
        @media (max-width: 767px) {
          .deal-params-grid {
            grid-template-columns: repeat(2, 1fr);
            row-gap: 20px;
          }
          .deal-param-item:nth-child(even) {
            border-left: 1px solid rgba(255,255,255,0.08);
          }
          /* Row 2 top separator */
          .deal-param-item:nth-child(3),
          .deal-param-item:nth-child(4) {
            padding-top: 16px;
            border-top: 1px solid rgba(255,255,255,0.06);
          }
        }
      `}</style>
    </div>);

}

// ─── Main Hero ────────────────────────────────────────────────────────────────

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const [mounted, setMounted] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setRevealed(true), 80);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: '#0D1F17' }}
      aria-label="Hero">

      {/* ── Main content ── */}
      {/* Mobile: pt-24 (tighter above headline), Desktop: pt-36 */}
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 sm:px-8 lg:px-12 pt-24 lg:pt-36 pb-16 lg:pb-20">

        {/* ── Top label row ── */}
        <div
          className="flex items-center gap-4 mb-10 lg:mb-16 transition-all duration-700"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'none' : 'translateY(12px)'
          }}>

          <div
            className="w-5 h-px"
            style={{ background: 'rgba(201,168,76,0.5)' }} />

          <span
            className="text-[9px] font-bold uppercase tracking-[0.45em]"
            style={{ color: 'rgba(201,168,76,0.75)', fontFamily: 'var(--font-body)' }}>EARNEST MONEY DEPOSIT SOULTIONS
          </span>
        </div>

        {/* ── Asymmetric editorial grid ── */}
        {/*
           Desktop: LEFT (7 cols) = headline + copy + CTAs + DealParams + sectors
                    RIGHT (5 cols) = QuickStart form
           Mobile:  single column — headline → copy → CTAs → form → DealParams → sectors
           
           Implementation: 3 grid items with CSS order to achieve mobile sequence
           without duplicating the form. Exactly ONE HeroLeadForm in the DOM.
          */}
        <div className="grid grid-cols-12 gap-x-6 lg:gap-x-10 items-start">

          {/* ITEM A — headline + subheadline + CTAs (order-1 on mobile, left col on desktop) */}
          <div className="col-span-12 lg:col-span-7 lg:pr-8" style={{ order: 1 }}>

            {/* Editorial headline */}
            <h1
              className="mb-8 lg:mb-10 font-display"
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'none' : 'translateY(20px)',
                transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.1s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.1s'
              }}>

              {/* Line 1 — large, light weight */}
              <span
                className="block font-display"
                style={{
                  fontSize: 'clamp(2.5rem, 6.5vw, 6rem)',
                  fontWeight: 300,
                  lineHeight: 0.95,
                  letterSpacing: '-0.03em',
                  color: 'rgba(255,255,255,0.92)'
                }}>
                Lock up the Deal.
              </span>
              {/* Line 2 — medium weight */}
              <span
                className="block font-display mt-3"
                style={{
                  fontSize: 'clamp(1.8rem, 4.8vw, 4.5rem)',
                  fontWeight: 500,
                  lineHeight: 0.95,
                  letterSpacing: '-0.03em',
                  color: 'rgba(255,255,255,0.88)',
                  paddingTop: '0.15em',
                  borderTop: '1px solid rgba(82,183,136,0.18)'
                }}>
                Keep your capital free.
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className="mb-10 lg:mb-12 leading-relaxed"
              style={{
                maxWidth: '440px',
                fontSize: 'clamp(1rem, 2.5vw, 1.0625rem)',
                color: 'rgba(255,255,255,0.80)',
                fontFamily: 'var(--font-body)',
                fontWeight: 400,
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'none' : 'translateY(16px)',
                transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.22s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.22s'
              }}>
              EarnestBridge funds your earnest money deposits so you can secure high-value deals requiring refundable deposits — without tying up your own capital.
            </p>

            {/* CTA row */}
            <div
              className="flex flex-wrap items-center gap-3 sm:gap-4"
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'none' : 'translateY(14px)',
                transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.34s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.34s'
              }}>

              <Link
                href="/contact"
                className="inline-block text-[10px] font-bold uppercase tracking-[0.25em] px-7 sm:px-8 py-4 transition-all duration-300 btn-primary"
                style={{
                  borderRadius: '6px',
                  fontFamily: 'var(--font-body)',
                  minHeight: '48px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}>
                Get Your EMD Funded
              </Link>
              <Link
                href="/how-it-works"
                className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.25em] px-7 sm:px-8 py-4 transition-all duration-300"
                style={{
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.70)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '6px',
                  fontFamily: 'var(--font-body)',
                  minHeight: '48px'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.95)';
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.40)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.70)';
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.22)';
                }}>
                See How It Works
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </div>

          {/* ITEM B — Quick Start form.
               Mobile: order-2 → appears after CTAs (item A order-1).
               Desktop: lg:col-span-5, lg:row-span-2 so it spans both left-column rows.
               Exactly ONE HeroLeadForm instance — no duplicate IDs. */}
          <div
            className="col-span-12 lg:col-span-5 lg:row-span-2 mt-10 lg:mt-0"
            style={{
              order: 2,
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'none' : 'translateY(20px)',
              transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.28s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.28s'
            }}>
            <HeroLeadForm revealed={revealed} />
          </div>

          {/* ITEM C — Spacer for left column second row on desktop (keeps grid alignment).
               On mobile this is hidden; Deal Parameters strip is full-width below. */}
          <div className="hidden lg:block lg:col-span-7" style={{ order: 3 }} aria-hidden="true" />
        </div>

        {/* ── Deal Parameters Strip — full width beneath both columns ── */}
        <div
          className="col-span-12"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'none' : 'translateY(14px)',
            transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.44s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.44s'
          }}>

          <DealParametersStrip revealed={revealed} />
        </div>

        {/* ── Sectors strip — below Deal Parameters ── */}
        <div
          className="flex items-center gap-3 sm:gap-4 flex-wrap"
          style={{
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            opacity: revealed ? 1 : 0,
            transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.56s'
          }}>

          <span
            className="text-[8px] font-bold uppercase tracking-[0.4em] flex-shrink-0"
            style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'var(--font-body)' }}>
            Sectors
          </span>
          <div className="w-4 h-px flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }} />
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
            {SECTORS.map((s) =>
            <span
              key={s}
              className="text-[10px] font-semibold uppercase tracking-[0.15em]"
              style={{
                color: 'rgba(255,255,255,0.72)',
                fontFamily: 'var(--font-body)'
              }}>
                {s}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>);

}